import { useCallback, useEffect, useMemo, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { Loader2, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";

/**
 * Tick lines, "Delete N lines", Undo (#1823 S4, mockup B).
 *
 * One request deletes every ticked line in one transaction; the server hands
 * the rows back exactly as stored and Undo puts those same rows back. Lines on
 * a per-device repair option are never emptied out from under the customer —
 * the server refuses and says which option (#1417).
 */
export function useBulkLineDelete(opts: {
    docType: "estimate" | "invoice";
    docId: string;
    lineIds: string[];
    onChanged: () => void | Promise<void>;
}) {
    const { docType, docId, lineIds, onChanged } = opts;
    const [selected, setSelected] = useState<Set<string>>(() => new Set());

    // Drop ticks for lines that are gone (deleted, regenerated, refetched).
    const idsKey = lineIds.join(",");
    useEffect(() => {
        setSelected((prev) => {
            const live = new Set(lineIds);
            const next = new Set([...prev].filter((id) => live.has(id)));
            return next.size === prev.size ? prev : next;
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [idsKey]);

    const toggle = useCallback((id: string) => {
        setSelected((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    }, []);
    const clear = useCallback(() => setSelected(new Set()), []);
    const selectAll = useCallback(() => setSelected(new Set(lineIds)), [lineIds]);

    const restore = useMutation({
        mutationFn: (lines: Record<string, unknown>[]) =>
            api.post<{ restored: number }>(`/api/${docType}s/${docId}/lines/restore`, { lines }),
        onSuccess: async (r) => {
            await onChanged();
            toast.success(`Put back ${r.restored} line${r.restored === 1 ? "" : "s"}`);
        },
        onError: (e: any) =>
            toast.error(`Undo failed: ${e?.body?.message ?? e?.body?.error ?? e?.message ?? "unknown"}`),
    });

    const remove = useMutation({
        mutationFn: (ids: string[]) =>
            api.post<{ deleted: Record<string, unknown>[] }>(`/api/${docType}s/${docId}/lines/bulk-delete`, {
                lineIds: ids,
            }),
        onSuccess: async (r) => {
            clear();
            await onChanged();
            const n = r.deleted.length;
            toast.success(`Deleted ${n} line${n === 1 ? "" : "s"}`, {
                duration: 12000,
                action: { label: "Undo", onClick: () => restore.mutate(r.deleted) },
            });
        },
        onError: (e: any) =>
            toast.error(
                e?.body?.error === "OPTION_WOULD_BE_EMPTY"
                    ? e.body.message
                    : `Lines NOT deleted: ${e?.body?.message ?? e?.body?.error ?? e?.message ?? "unknown"}`,
            ),
    });

    return useMemo(
        () => ({
            selected,
            toggle,
            clear,
            selectAll,
            total: lineIds.length,
            deleteSelected: () => remove.mutate([...selected]),
            /** One tap, no "are you sure": the toast's Undo puts it back (GWC lines, slice C). */
            removeOne: (lineId: string) => remove.mutate([lineId]),
            pending: remove.isPending || restore.isPending,
        }),
        [selected, toggle, clear, selectAll, lineIds.length, remove, restore],
    );
}

export type BulkLineDelete = ReturnType<typeof useBulkLineDelete>;

/** The per-row tick box. */
export function LineCheckbox({ bulk, id, label }: { bulk: BulkLineDelete; id: string; label?: string }) {
    return (
        <input
            type="checkbox"
            className="h-4 w-4 cursor-pointer accent-primary"
            aria-label={label ? `Select line: ${label}` : "Select line"}
            checked={bulk.selected.has(id)}
            onChange={() => bulk.toggle(id)}
            data-testid="line-select"
        />
    );
}

/** The dark "N lines selected · Cancel · Delete N lines" bar. */
export function BulkDeleteBar({ bulk }: { bulk: BulkLineDelete }) {
    const n = bulk.selected.size;
    if (n === 0) return null;
    const noun = `${n} line${n === 1 ? "" : "s"}`;
    return (
        <div
            className="sticky bottom-3 z-10 mt-2 flex flex-wrap items-center justify-between gap-3 rounded-lg bg-shell px-4 py-2.5 text-shell-foreground shadow-lg"
            data-testid="bulk-delete-bar"
        >
            <span className="text-sm font-semibold">
                {noun} selected
                {n < bulk.total && (
                    <button type="button" className="ml-3 text-xs font-medium underline" onClick={bulk.selectAll}>
                        Select all {bulk.total}
                    </button>
                )}
            </span>
            <div className="flex items-center gap-2">
                <Button
                    size="sm"
                    variant="outline"
                    className="border-shell-foreground/40 bg-transparent text-shell-foreground hover:bg-shell-foreground/10"
                    onClick={bulk.clear}
                    disabled={bulk.pending}
                >
                    Cancel
                </Button>
                <Button size="sm" variant="destructive" onClick={bulk.deleteSelected} disabled={bulk.pending}>
                    {bulk.pending ? (
                        <Loader2 className="mr-1 h-4 w-4 animate-spin" />
                    ) : (
                        <Trash2 className="mr-1 h-4 w-4" />
                    )}
                    Delete {noun}
                </Button>
            </div>
        </div>
    );
}
